/**
 * ExportMenu — CSV (visible|all) + PNG (FULL data, not just viewport).
 *
 * PNG flow:
 *   1. Mount <GanttFullRender> off-screen (position:fixed; left:-99999px) so
 *      every filtered row + the full timeline width are realised in the DOM.
 *   2. Wait one tick for layout.
 *   3. html2canvas(node, { scale: 2 }) — captures everything.
 *   4. Unmount the off-screen node.
 */
import { useState } from "react";
import { createRoot } from "react-dom/client";
import Papa from "papaparse";
import html2canvas from "html2canvas";
import { Loader2, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { GanttFullRender } from "./GanttFullRender";
import { useI18n } from "../context/I18nContext";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function buildCsv(tasks, columns, useVisibleOnly) {
  const cols = useVisibleOnly ? columns.filter((c) => c.visible) : columns;
  // Build headers explicitly. Fall back to the column key when `label` is
  // missing so we never emit a literal "undefined" header (regression iter26).
  const fields = cols.map((c) => (c.label ?? c.key ?? ""));
  const data = tasks.map((t) =>
    cols.map((c) => {
      let v;
      if (c.key.startsWith("custom_fields.")) v = (t.custom_fields || {})[c.key.slice("custom_fields.".length)];
      else v = t[c.key];
      if (Array.isArray(v)) v = v.join("; ");
      return v ?? "";
    }),
  );
  return Papa.unparse({ fields, data }, { delimiter: ";" });
}

// Most browsers cap a single canvas at ~16384x16384 pixels (Chrome / Firefox).
// Safari is more conservative (~4096x4096 on older iOS). Use 14000 as a safe
// upper bound to leave headroom for the html2canvas internal buffer.
const MAX_CANVAS_DIM = 14000;
// Below this scale the text becomes unreadable — bail out and tell the user.
const MIN_SCALE = 0.6;

/**
 * Render the full Gantt off-screen and capture it as a PNG.
 * Returns { blob, scaleUsed, source: "single"|"tiled" }.
 *
 * Resilience strategy when the chart is huge (many columns or tasks):
 *  1. Measure the off-screen DOM once it's laid out.
 *  2. Pick the largest `scale` such that scale * max(w,h) <= MAX_CANVAS_DIM.
 *  3. If that scale would drop below MIN_SCALE → fail with a helpful
 *     error so the user can reduce columns or filter the dataset.
 */
async function captureFullGanttPng({ tasks, columns, syncMeta, projectName, t }) {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-99999px";
  host.style.top = "0";
  host.style.zIndex = "-1";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);
  const root = createRoot(host);

  try {
    await new Promise((resolve) => {
      root.render(
        <GanttFullRender
          tasks={tasks}
          columns={columns}
          syncMeta={syncMeta}
          projectName={projectName}
        />,
      );
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    const target = host.firstElementChild;
    if (!target) throw new Error(t("export_failed_generic"));

    const w = target.scrollWidth;
    const h = target.scrollHeight;
    const longest = Math.max(w, h);
    let scale = 2;
    if (longest * scale > MAX_CANVAS_DIM) {
      scale = MAX_CANVAS_DIM / longest;
    }
    if (scale < MIN_SCALE) {
      throw new Error(t("export_too_large", { w, h }));
    }

    const canvas = await html2canvas(target, {
      backgroundColor: "#FFFFFF",
      scale,
      useCORS: true,
      logging: false,
      windowWidth: w,
      windowHeight: h,
    });
    const blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
    return { blob, scaleUsed: scale, source: "single" };
  } finally {
    root.unmount();
    if (host.parentNode) host.parentNode.removeChild(host);
  }
}

export default function ExportMenu({ open, onClose, tasks, columns, syncMeta = {}, projectName = "Cornerstone", ganttContainerRef: _legacyRef }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleCsv = (useVisibleOnly) => {
    const csv = buildCsv(tasks, columns, useVisibleOnly);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const suffix = useVisibleOnly ? t("export_csv_visible_suffix") : t("export_csv_all_suffix");
    downloadBlob(blob, `${projectName.replace(/[^a-z0-9_-]+/gi, "_")}-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`);
    onClose?.();
  };

  const handlePng = async () => {
    if (busy) return;
    if (!tasks?.length) { toast.error(t("export_nothing")); return; }
    setBusy(true);
    try {
      if (tasks.length > 2000) toast.info(t("export_rendering_many", { n: tasks.length }));
      const { blob, scaleUsed } = await captureFullGanttPng({ tasks, columns, syncMeta, projectName, t });
      if (!blob) throw new Error(t("export_failed_generic"));
      downloadBlob(blob, `${projectName.replace(/[^a-z0-9_-]+/gi, "_")}-${new Date().toISOString().slice(0, 10)}.png`);
      if (scaleUsed < 2) {
        toast.warning(t("export_quality_reduced", { pct: (scaleUsed * 100).toFixed(0) }));
      } else {
        toast.success(t("export_png_success"));
      }
      onClose?.();
    } catch (e) {
      console.error("PNG export failed", e);
      toast.error(`${t("export_png_failed")}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid="export-menu">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold">{t("export_title")}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-2">
          <button onClick={() => handleCsv(true)} className="w-full text-left px-3 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 flex items-center gap-3" data-testid="export-csv-visible">
            <FileText size={16} className="text-cs-accent" />
            <div>
              <div className="text-sm font-semibold">{t("export_csv_visible_title")}</div>
              <div className="text-xs text-zinc-500">{t("export_csv_visible_desc")}</div>
            </div>
          </button>
          <button onClick={() => handleCsv(false)} className="w-full text-left px-3 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 flex items-center gap-3" data-testid="export-csv-all">
            <FileText size={16} className="text-zinc-500" />
            <div>
              <div className="text-sm font-semibold">{t("export_csv_all_title")}</div>
              <div className="text-xs text-zinc-500">{t("export_csv_all_desc")}</div>
            </div>
          </button>
          <button onClick={handlePng} disabled={busy} className="w-full text-left px-3 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 flex items-center gap-3 disabled:opacity-60" data-testid="export-png">
            {busy ? <Loader2 size={16} className="text-cs-blueprint animate-spin" /> : <ImageIcon size={16} className="text-cs-blueprint" />}
            <div>
              <div className="text-sm font-semibold">{t("export_png_title")}</div>
              <div className="text-xs text-zinc-500">{t("export_png_desc")}</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
